import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Loader2,
  LogOut,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Users,
  Search,
  ShieldCheck,
  ShieldAlert,
  Download,
  RefreshCw,
  Inbox,
  GalleryHorizontal,
} from "lucide-react";
import GalleryEditor from "@/pages/GalleryEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bonevento_admin_token";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Field = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon size={14} className="text-[#B8935C] mt-1 flex-shrink-0" strokeWidth={1.4} />
      <div>
        <div className="overline text-[9px] text-[#F0EBE1]/45 mb-0.5">
          {label}
        </div>
        <div className="text-[#F0EBE1]/90 text-sm font-light leading-snug break-words">
          {value}
        </div>
      </div>
    </div>
  );
};

const AdminLogin = ({ onLogin }) => {
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/login`, { token: token.trim() });
      localStorage.setItem(STORAGE_KEY, token.trim());
      onLogin(token.trim());
    } catch (err) {
      toast.error("Incorrect password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080B12] text-[#F0EBE1] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-[#121623] border border-[#F0EBE1]/10 p-8 md:p-10"
      >
        <div className="overline mb-4">Bon Evento · Studio Access</div>
        <h1 className="font-serif text-3xl md:text-4xl font-light leading-tight mb-3">
          Admin <span className="italic text-[#B8935C]">Dashboard</span>
        </h1>
        <p className="text-[#F0EBE1]/55 text-sm font-light leading-relaxed mb-8">
          Enter your studio password to view every enquiry submitted across the
          Bon Evento website.
        </p>

        <label className="overline block mb-1" htmlFor="admin-token">
          Studio Password
        </label>
        <input
          id="admin-token"
          name="admin-token"
          type="password"
          autoComplete="current-password"
          data-testid="admin-token-input"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="••••••••••••"
          className="input-editorial"
        />

        <button
          type="submit"
          disabled={submitting}
          data-testid="admin-login-btn"
          className="mt-8 w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#B8935C] text-[#080B12] text-xs uppercase tracking-[0.28em] font-medium hover:bg-[#D4AF37] transition-all duration-500 disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Verifying
            </>
          ) : (
            <>Enter Studio</>
          )}
        </button>
      </form>
    </div>
  );
};

const AdminDashboard = () => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("inquiries");

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": t };
      const [list, st] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setInquiries(list.data || []);
      setStats(st.data || null);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        setToken("");
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Could not load enquiries. Please retry.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => {
    if (!search.trim()) return inquiries;
    const q = search.trim().toLowerCase();
    return inquiries.filter((i) =>
      [
        i.name,
        i.email,
        i.phone,
        i.event_type,
        i.event_date,
        i.location,
        i.guest_count,
        i.message,
      ]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [inquiries, search]);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setInquiries([]);
    setStats(null);
  };

  const downloadCsv = () => {
    if (inquiries.length === 0) return;
    const headers = [
      "Created",
      "Name",
      "Email",
      "Phone",
      "Event Type",
      "Event Date",
      "Location",
      "Guests",
      "Message",
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = inquiries.map((i) =>
      [
        formatDate(i.created_at),
        i.name,
        i.email,
        i.phone,
        i.event_type,
        i.event_date,
        i.location,
        i.guest_count,
        i.message,
      ]
        .map(escape)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bonevento-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <>
        <Toaster
          position="bottom-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "#121623",
              color: "#F0EBE1",
              border: "1px solid rgba(240,235,225,0.1)",
              fontFamily: "'Outfit', sans-serif",
            },
          }}
        />
        <AdminLogin onLogin={setToken} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#080B12] text-[#F0EBE1]">
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#121623",
            color: "#F0EBE1",
            border: "1px solid rgba(240,235,225,0.1)",
            fontFamily: "'Outfit', sans-serif",
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-[#F0EBE1]/10 sticky top-0 z-30 bg-[#080B12]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span
              className="font-serif text-xl md:text-2xl text-[#F0EBE1] tracking-[0.04em] leading-none"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Bon&nbsp;<span className="italic text-[#B8935C]">Evento</span>
            </span>
            <div className="overline text-[10px]">Studio · Enquiries</div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => load()}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 px-3 md:px-4 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/75 text-[10px] uppercase tracking-[0.24em] hover:border-[#B8935C] hover:text-[#B8935C] transition-colors"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={downloadCsv}
              data-testid="admin-csv-btn"
              className="inline-flex items-center gap-2 px-3 md:px-4 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/75 text-[10px] uppercase tracking-[0.24em] hover:border-[#B8935C] hover:text-[#B8935C] transition-colors"
            >
              <Download size={12} />
              <span className="hidden sm:inline">CSV</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 px-3 md:px-4 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/75 text-[10px] uppercase tracking-[0.24em] hover:border-[#6A1A25] hover:text-[#F0EBE1] hover:bg-[#6A1A25] transition-colors"
            >
              <LogOut size={12} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-10 border-b border-[#F0EBE1]/10">
          {[
            { id: "inquiries", label: "Enquiries", icon: Inbox },
            { id: "gallery", label: "Weddings Gallery", icon: GalleryHorizontal },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-5 py-3 text-[10px] uppercase tracking-[0.26em] border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-[#B8935C] text-[#B8935C]"
                    : "border-transparent text-[#F0EBE1]/55 hover:text-[#F0EBE1]"
                }`}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "gallery" ? (
          <GalleryEditor token={token} />
        ) : (
          <>
            {/* Hero stats */}
        <div className="mb-10 md:mb-14">
          <div className="overline mb-4">Overview</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight">
            Every <span className="italic text-[#B8935C]">enquiry</span> in one place.
          </h1>

          {stats && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {[
                { label: "Total Enquiries", value: stats.total },
                { label: "Last 7 Days", value: stats.last_7_days },
                {
                  label: "Email Delivery",
                  value: stats.email_configured ? "Active" : "Off",
                  warn: !stats.email_configured,
                  meta: stats.email_configured ? `→ ${stats.notify_email}` : "Set RESEND_API_KEY",
                },
                {
                  label: "Google Sheet",
                  value: stats.sheet_configured ? "Synced" : "Off",
                  warn: !stats.sheet_configured,
                  meta: stats.sheet_configured
                    ? "Every enquiry appended"
                    : "Set GOOGLE_SHEET_ID + service account",
                },
              ].map((s) => (
                <div
                  key={s.label}
                  data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="bg-[#121623] border border-[#F0EBE1]/10 p-5 md:p-6"
                >
                  <div className="overline text-[10px] mb-3">{s.label}</div>
                  <div
                    className={`font-serif text-3xl md:text-4xl font-light ${
                      s.warn ? "text-[#6A1A25]" : "text-[#B8935C]"
                    }`}
                  >
                    {s.value}
                  </div>
                  {s.meta && (
                    <div className="mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F0EBE1]/45 break-all">
                      {s.warn ? (
                        <ShieldAlert size={12} className="text-[#6A1A25] flex-shrink-0" />
                      ) : (
                        <ShieldCheck size={12} className="text-[#B8935C] flex-shrink-0" />
                      )}
                      <span className="break-all">{s.meta}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search
              size={14}
              className="absolute left-0 top-1/2 -translate-y-1/2 text-[#F0EBE1]/35"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
              placeholder="Search by name, email, phone, event…"
              className="input-editorial pl-6"
            />
          </div>
          <div className="overline text-[10px] text-[#F0EBE1]/55">
            Showing {filtered.length} of {inquiries.length}
          </div>
        </div>

        {/* List */}
        {loading && inquiries.length === 0 ? (
          <div className="flex items-center justify-center py-24 text-[#F0EBE1]/45">
            <Loader2 size={20} className="animate-spin mr-3" /> Loading enquiries…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center text-[#F0EBE1]/45 font-light">
            No enquiries match your search yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {filtered.map((i) => (
              <article
                key={i.id}
                data-testid={`inquiry-${i.id}`}
                className="bg-[#121623] border border-[#F0EBE1]/10 p-6 md:p-7 hover:border-[#B8935C]/40 transition-colors duration-500"
              >
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <h3 className="font-serif text-2xl text-[#F0EBE1] leading-tight">
                      {i.name}
                    </h3>
                    {i.event_type && (
                      <div className="overline text-[10px] mt-1.5 text-[#B8935C]">
                        {i.event_type}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#F0EBE1]/45">
                      {formatDate(i.created_at)}
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-5 gap-y-3 mb-5">
                  <Field icon={Mail} label="Email" value={i.email} />
                  <Field icon={Phone} label="Phone" value={i.phone} />
                  <Field icon={Calendar} label="Event Date" value={i.event_date} />
                  <Field icon={MapPin} label="Location" value={i.location} />
                  <Field icon={Users} label="Guests" value={i.guest_count} />
                </div>

                {i.message && (
                  <div className="pt-5 border-t border-[#F0EBE1]/10">
                    <div className="overline text-[9px] mb-2">Message</div>
                    <p className="text-[#F0EBE1]/75 text-sm leading-relaxed font-light whitespace-pre-wrap">
                      {i.message}
                    </p>
                  </div>
                )}

                <div className="pt-5 mt-5 border-t border-[#F0EBE1]/10 flex flex-wrap gap-2">
                  {i.email && (
                    <a
                      href={`mailto:${i.email}?subject=Re%3A%20Your%20enquiry%20with%20Bon%20Evento`}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/80 text-[10px] uppercase tracking-[0.24em] hover:border-[#B8935C] hover:text-[#B8935C] transition-colors"
                    >
                      <Mail size={12} /> Reply
                    </a>
                  )}
                  {i.phone && (
                    <a
                      href={`https://wa.me/${i.phone.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/80 text-[10px] uppercase tracking-[0.24em] hover:border-[#B8935C] hover:text-[#B8935C] transition-colors"
                    >
                      <Phone size={12} /> WhatsApp
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
